import { S3Client } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { Upload } from '@aws-sdk/lib-storage';
import type { AwsCredentialIdentity, Provider } from '@aws-sdk/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import { format } from 'date-fns';
import { generate } from 'shortid';
import type { DivClassType } from 'types/divClassType';

/* ----------------------------------------------------------------------- */
/* Validation関連 */
/* ----------------------------------------------------------------------- */
/**
 * 値の存在検証をします。
 */
export function isEmpty(input: unknown): boolean {
  if (input === null || input === undefined) {
    return true;
  } else if (input === '') {
    return true;
  } else if (Array.isArray(input) && input.length === 0) {
    return true;
  } else if (typeof input === 'object' && Object.keys(input as object).length === 0) {
    return true;
  }
  return false;
}

/**
 * 値の存在検証をします。
 */
export function isNotEmpty(input: unknown): boolean {
  return !isEmpty(input);
}

/**
 * URLフォーマットの検証をします。
 */
export function isUrl(input: string): boolean {
  let url;
  try {
    url = new URL(input);
  } catch {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
}

/* ----------------------------------------------------------------------- */
/* 区分定数関連 */
/* ----------------------------------------------------------------------- */
/**
 * 区分値のラベルを取得します。
 */
export function getDivLabel(divClass: DivClassType, value: number): string {
  const entry = Object.values(DivConst.items[divClass]).find(item => item.value === value);
  return entry?.label ?? '';
}

/**
 * 区分値の存在検証をします。
 */
export function isDivValue(divClass: DivClassType, value: number): boolean {
  return Object.values(DivConst.items[divClass]).some(v => v.value === value);
}

/**
 * 選択オプション用の区分値の配列を返します。
 */
export function getDivSelectOptions(divClass: DivClassType): { value: string; label: string }[] {
  return Object.values(DivConst.items[divClass]).map(item => ({
    value: item.value.toString(),
    label: item.label,
  }));
}

/* ----------------------------------------------------------------------- */
/* 画面制御関連 */
/* ----------------------------------------------------------------------- */
/**
 * 二重送信を防止します。
 */
export async function submit(
  processing: boolean,
  setProcessing: (processing: boolean) => void,
  callback: { (): Promise<void> }
): Promise<void> {
  if (processing) {
    return;
  }
  setProcessing(true);
  await callback();
  setProcessing(false);
  return;
}

/* ----------------------------------------------------------------------- */
/* 画面表示関連 */
/* ----------------------------------------------------------------------- */
/**
 * カンパニー画像のURLを取得します。
 */
export function getCompanyImageUrl(imageKey: string): string {
  if (imageKey) {
    return _convS3KeyToUrl(imageKey);
  }
  return `${config.s3.bucket.staticContents.endpoint}/sys/default/company.png`;
}

/**
 * クイズ画像のURLを取得します。
 */
export function getContentImageUrl(imageKey: string): string {
  if (imageKey) {
    return _convS3KeyToUrl(imageKey);
  }
  return `${config.s3.bucket.staticContents.endpoint}/sys/default/quiz.jpg`;
}

/**
 * S3のパスをCDNのURLに変換します。
 */
export function _convS3KeyToUrl(imageKey: string | null): string {
  if (!imageKey) {
    return null;
  }
  return `${config.s3.bucket.staticContents.endpoint}${imageKey.replace('public/', '/')}`;
}

/**
 * S3にBlob形式のファイルをアップロードします。
 * ContentType を明示的に設定することで、S3 のデフォルト（application/octet-stream）を回避します。
 */
export async function uploadS3(blob: Blob, extension: string): Promise<string> {
  if (!blob) {
    return null;
  }
  // ファイル名の設定
  const filePath = `${getUid(28)}.${extension}`;
  // S3オブジェクト作成
  const s3Client = new S3Client({
    credentials: await getAwsCredential(),
    region: config.cognito.region,
  });
  // ファイルのアップロード（ContentType を明示して S3 のデフォルト "application/octet-stream" を回避）
  const upload = new Upload({
    client: s3Client,
    params: {
      Bucket: config.s3.bucket.upload.name,
      Key: filePath,
      Body: blob,
      ContentType: blob.type || 'application/octet-stream',
    },
  });
  upload.on('httpUploadProgress', progress => {
    console.debug(progress);
  });
  await upload.done();

  return filePath;
}

/**
 * AWSの認証情報を設定します。
 */
export async function getAwsCredential(): Promise<Provider<AwsCredentialIdentity>> {
  // 人情情報を取得
  const session = await fetchAuthSession();

  // PROVIDER_KEY作成
  const providerKey =
    'cognito-idp.' + config.cognito.region + '.amazonaws.com/' + config.cognito.userPoolId;

  // IDプールにてIDトークンの認証を行う
  return fromCognitoIdentityPool({
    clientConfig: { region: config.cognito.region },
    identityPoolId: config.cognito.identityPoolId,
    logins: {
      [providerKey]: session.tokens.idToken.toString(),
    },
  });
}

/* ----------------------------------------------------------------------- */
/* 画面フォーマット関連 */
/* ----------------------------------------------------------------------- */
/**
 * 数値を3桁ごとにカンマ区切りにします。
 */
export function formatNumberToCurrency(input: number): string {
  if (input == null) {
    return null;
  }
  return input.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 割合をパーセント表示にします。
 * 第二引数が指定された場合は。第一引数と第二引数を除算した値のパーセント表示にします。
 */
export function formatRateToPercent(numerator: number, denominator?: number): number {
  if (numerator == null) {
    return null;
  }
  // 第二引数が指定された場合は。第一引数と第二引数を除算した値のパーセント表示にします。
  const denominatorVal = denominator ?? 1;
  const rate = numerator && denominatorVal ? numerator / denominatorVal : 0;

  // 小数点以下を四捨五入して整数にする
  return Math.round(rate * 100);
}

/**
 * 獲得リスト数（メールアドレス獲得数 + LINE獲得数）を算出します。
 * 会社全体を横断する集計（複数コンテンツの合計）でのみ使用します。
 */
export function getAcquiredListCount(mailAddressCount: number, lineCount: number): number {
  return mailAddressCount + lineCount;
}

/**
 * コンテンツ単位の獲得リスト数を、lineFlag に応じてメール/LINEいずれか一方から取得します。
 * 同一コンテンツ内でメールとLINEを合算しないための出し分けに使用します。
 */
export function getChannelAcquiredCount(
  lineFlag: boolean,
  mailAddressCount: number,
  lineCount: number
): number {
  return lineFlag ? lineCount : mailAddressCount;
}

/**
 * コンテンツ単位の獲得率を、lineFlag に応じてメール/LINEいずれか一方から取得します。
 */
export function getChannelAcquisitionRate(
  lineFlag: boolean,
  mailAddressAcquisitionRate: number,
  lineAddressAcquisitionRate: number
): number {
  return lineFlag ? lineAddressAcquisitionRate : mailAddressAcquisitionRate;
}

/**
 * ミリ秒を日付フォーマットに変換します。
 */
export function formatTimeToDate(milliseconds: number, dateFormat: string): string {
  if (milliseconds == null) {
    return null;
  }
  const date = new Date(milliseconds);
  return format(date, dateFormat);
}

/**
 * バイト数をフォーマットに変換します。
 */
export function formatBytes(bytes: number): string {
  if (bytes == null) {
    return null;
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let i = 0;

  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024;
    i++;
  }

  return `${bytes.toFixed(2)} ${units[i]}`;
}

/* ----------------------------------------------------------------------- */
/* 画像関連ユーティリティ */
/* ----------------------------------------------------------------------- */
/**
 * ファイル名から画像形式を取得します。
 */
export function getImageType(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'JPEG';
    case 'png':
      return 'PNG';
    case 'gif':
      return 'GIF';
    default:
      return '';
  }
}

/* ----------------------------------------------------------------------- */
/* 共通ユーティリティ */
/* ----------------------------------------------------------------------- */
/**
 * UIDを発行します。
 */
export function getUid(digits: number): string {
  let uuid = '';
  while (uuid.length < digits) {
    uuid += generate();
  }
  return uuid.substring(0, digits);
}
